import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, Mail, FileText, Inbox, LogOut, Plus, Pencil, Trash2, ExternalLink, X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STATUS = ["new", "contacted", "won", "lost"];
const statusColor = {
  new: "bg-dq-orange/10 text-dq-orange",
  contacted: "bg-[#FFB800]/15 text-[#a97a00]",
  won: "bg-dq-success/10 text-dq-success",
  lost: "bg-dq-muted/10 text-dq-muted",
};

const emptyPost = { title: "", excerpt: "", content: "", category: "Marketing", cover_image: "", read_time: "5 min read", author: "DiziTeQ Team", published: true };

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ leads: 0, new_leads: 0, subscribers: 0, posts: 0 });
  const [leads, setLeads] = useState([]);
  const [subs, setSubs] = useState([]);
  const [posts, setPosts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyPost);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, l, n, b] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/leads"),
        api.get("/admin/newsletter"), api.get("/blog"),
      ]);
      setStats(s.data); setLeads(l.data); setSubs(n.data); setPosts(b.data);
    } catch (err) {
      toast.error("Failed to load data");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doLogout = () => { logout(); navigate("/admin/login"); };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success("Status updated");
    } catch { toast.error("Update failed"); }
  };

  const openNew = () => { setEditing(null); setForm(emptyPost); setDialogOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...emptyPost, ...p }); setDialogOpen(true); };

  const savePost = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (editing) await api.put(`/admin/blog/${editing.id}`, payload);
      else await api.post("/admin/blog", payload);
      toast.success(editing ? "Post updated" : "Post created");
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const deletePost = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try { await api.delete(`/admin/blog/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const statCards = [
    { icon: Inbox, label: "Total Leads", value: stats.leads },
    { icon: Users, label: "New Leads", value: stats.new_leads },
    { icon: Mail, label: "Subscribers", value: stats.subscribers },
    { icon: FileText, label: "Blog Posts", value: stats.posts },
  ];
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const inputCls = "w-full rounded-xl border border-dq-line bg-white px-4 py-2.5 text-sm focus:border-dq-orange focus:outline-none";

  return (
    <div className="min-h-screen bg-dq-bg">
      {/* Top bar */}
      <header className="sticky top-0 z-30 border-b border-dq-line bg-white/80 backdrop-blur-xl">
        <div className="dq-container flex items-center justify-between py-4">
          <div className="flex items-center gap-2.5">
            <span className="grid h-9 w-9 place-items-center rounded-xl bg-dq-ink font-display text-lg font-extrabold text-white">D</span>
            <div>
              <div className="font-display text-sm font-extrabold text-dq-ink">DiziTeQ Admin</div>
              <div className="text-xs text-dq-muted">{user?.email}</div>
            </div>
          </div>
          <button onClick={doLogout} data-testid="admin-logout" className="btn-secondary py-2.5 text-sm">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </header>

      <main className="dq-container py-10">
        <h1 className="font-display text-3xl font-extrabold text-dq-ink">Dashboard</h1>

        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {statCards.map((c) => (
            <div key={c.label} className="rounded-2xl border border-dq-line bg-white p-5" data-testid={`stat-${c.label.toLowerCase().replace(/\s/g, "-")}`}>
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-dq-orange/10 text-dq-orange">
                <c.icon className="h-5 w-5" />
              </span>
              <div className="mt-3 font-display text-3xl font-extrabold text-dq-ink">{c.value}</div>
              <div className="text-sm text-dq-muted">{c.label}</div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="leads" className="mt-10">
          <TabsList className="bg-white">
            <TabsTrigger value="leads" data-testid="tab-leads">Leads</TabsTrigger>
            <TabsTrigger value="subscribers" data-testid="tab-subscribers">Subscribers</TabsTrigger>
            <TabsTrigger value="blog" data-testid="tab-blog">Blog</TabsTrigger>
          </TabsList>

          {/* Leads */}
          <TabsContent value="leads">
            <div className="overflow-hidden rounded-2xl border border-dq-line bg-white" data-testid="leads-table">
              {leads.length === 0 ? (
                <p className="p-8 text-center text-dq-muted">No leads yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="border-b border-dq-line bg-dq-bg text-xs uppercase tracking-wider text-dq-muted">
                      <tr>
                        <th className="px-5 py-3">Name</th>
                        <th className="px-5 py-3">Contact</th>
                        <th className="px-5 py-3">Service</th>
                        <th className="px-5 py-3">Source</th>
                        <th className="px-5 py-3">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leads.map((l) => (
                        <tr key={l.id} className="border-b border-dq-line/60 last:border-0" data-testid={`lead-row-${l.id}`}>
                          <td className="px-5 py-4">
                            <div className="font-semibold text-dq-ink">{l.name}</div>
                            {l.company && <div className="text-xs text-dq-muted">{l.company}</div>}
                          </td>
                          <td className="px-5 py-4 text-dq-muted">
                            <div>{l.email}</div>
                            <div className="text-xs">{l.phone}</div>
                          </td>
                          <td className="px-5 py-4 text-dq-muted">{l.service || "—"}</td>
                          <td className="px-5 py-4"><span className="rounded-full bg-dq-bg px-2.5 py-1 text-xs text-dq-muted">{l.source}</span></td>
                          <td className="px-5 py-4">
                            <Select value={l.status} onValueChange={(v) => updateStatus(l.id, v)}>
                              <SelectTrigger className={`h-8 w-32 rounded-full border-0 text-xs font-semibold ${statusColor[l.status] || statusColor.new}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {STATUS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Subscribers */}
          <TabsContent value="subscribers">
            <div className="overflow-hidden rounded-2xl border border-dq-line bg-white" data-testid="subs-table">
              {subs.length === 0 ? (
                <p className="p-8 text-center text-dq-muted">No subscribers yet.</p>
              ) : (
                <ul className="divide-y divide-dq-line/60">
                  {subs.map((s) => (
                    <li key={s.id} className="flex items-center justify-between px-5 py-3.5 text-sm">
                      <span className="font-medium text-dq-ink">{s.email}</span>
                      <span className="text-xs text-dq-muted">{new Date(s.created_at).toLocaleDateString()}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </TabsContent>

          {/* Blog */}
          <TabsContent value="blog">
            <div className="mb-4 flex justify-end">
              <button onClick={openNew} data-testid="blog-new" className="btn-primary py-2.5 text-sm">
                <Plus className="h-4 w-4" /> New Post
              </button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {posts.map((p) => (
                <div key={p.id} className="rounded-2xl border border-dq-line bg-white p-5" data-testid={`admin-post-${p.id}`}>
                  <span className="rounded-full bg-dq-orange/10 px-2.5 py-1 text-xs font-semibold text-dq-orange">{p.category}</span>
                  <h3 className="mt-3 font-display font-bold leading-snug text-dq-ink">{p.title}</h3>
                  <p className="mt-1.5 line-clamp-2 text-sm text-dq-muted">{p.excerpt}</p>
                  <div className="mt-4 flex gap-2">
                    <button onClick={() => openEdit(p)} data-testid={`edit-post-${p.id}`} className="flex items-center gap-1.5 rounded-lg border border-dq-line px-3 py-1.5 text-xs font-semibold text-dq-ink hover:border-dq-ink">
                      <Pencil className="h-3.5 w-3.5" /> Edit
                    </button>
                    <button onClick={() => deletePost(p.id)} data-testid={`delete-post-${p.id}`} className="flex items-center gap-1.5 rounded-lg border border-dq-line px-3 py-1.5 text-xs font-semibold text-dq-orange hover:border-dq-orange">
                      <Trash2 className="h-3.5 w-3.5" /> Delete
                    </button>
                    <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="ml-auto grid h-8 w-8 place-items-center rounded-lg border border-dq-line text-dq-muted hover:text-dq-ink">
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit Post" : "New Post"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={savePost} className="space-y-4" data-testid="blog-form">
            <input required placeholder="Title" value={form.title} onChange={(e) => setF("title", e.target.value)} className={inputCls} data-testid="post-title" />
            <input required placeholder="Excerpt" value={form.excerpt} onChange={(e) => setF("excerpt", e.target.value)} className={inputCls} data-testid="post-excerpt" />
            <div className="grid gap-4 sm:grid-cols-2">
              <input placeholder="Category" value={form.category} onChange={(e) => setF("category", e.target.value)} className={inputCls} />
              <input placeholder="Read time" value={form.read_time} onChange={(e) => setF("read_time", e.target.value)} className={inputCls} />
            </div>
            <input placeholder="Cover image URL (optional)" value={form.cover_image} onChange={(e) => setF("cover_image", e.target.value)} className={inputCls} />
            <textarea required rows={8} placeholder="Content (use ## for headings)" value={form.content} onChange={(e) => setF("content", e.target.value)} className={inputCls} data-testid="post-content" />
            <button type="submit" disabled={saving} data-testid="post-save" className="btn-primary w-full disabled:opacity-60">
              {saving ? "Saving..." : editing ? "Update Post" : "Create Post"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
